import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import type { CategoryRow, EventTypeRow, SyncFields } from '@/db/types'
import { getDeviceId } from './syncStateRepository'
import { reassignType } from './entryRepository'
import type { ChangeSet } from './entryRepository'
import { normaliseHex, suggestColor } from '@/design/palette'
import { logger } from '@/core/logging/logger'

/**
 * The catalogue: categories and event types.
 *
 * The only module that writes `categories` and `eventTypes`, for the same
 * reason entryRepository owns `entries` -- every write has to set updatedAt,
 * deviceId and _dirty, and a single missed field means a row that silently
 * never syncs.
 *
 * Two rules run through everything here:
 *
 *   Nothing destructive ever loses tracked time. Archiving hides a type from
 *   the picker but leaves every entry pointing at it, so statistics stay
 *   honest. Deleting a type is refused outright while entries still use it --
 *   you are offered Archive or Merge instead.
 *
 *   Deleting a category never cascades to its types. They become uncategorised
 *   and keep working. A cascade would turn tidying a list into losing a year
 *   of history.
 */

/** Thrown when a delete would destroy history. Carries the count so the UI can say so. */
export class CatalogGuardError extends Error {
  constructor(
    message: string,
    readonly entryCount: number,
  ) {
    super(message)
    this.name = 'CatalogGuardError'
  }
}

// --- Reading ----------------------------------------------------------------

export async function listCategories(
  includeArchived = false,
  db: BlockyTimeDB = defaultDb,
): Promise<CategoryRow[]> {
  const rows = await db.categories.orderBy('sortOrder').toArray()
  return rows.filter((r) => r.deletedAt === null && (includeArchived || r.isArchived === 0))
}

export async function listEventTypes(
  includeArchived = false,
  db: BlockyTimeDB = defaultDb,
): Promise<EventTypeRow[]> {
  const rows = await db.eventTypes.orderBy('sortOrder').toArray()
  return rows.filter((r) => r.deletedAt === null && (includeArchived || r.isArchived === 0))
}

/** How many live blocks point at a type. Shown before anything destructive. */
export async function countEntriesForType(
  eventTypeId: string,
  db: BlockyTimeDB = defaultDb,
): Promise<number> {
  const rows = await db.entries.where('eventTypeId').equals(eventTypeId).toArray()
  return rows.filter((r) => r.deletedAt === null).length
}

/** Live blocks across every type in a category. */
export async function countEntriesForCategory(
  categoryId: string,
  db: BlockyTimeDB = defaultDb,
): Promise<number> {
  const types = await db.eventTypes.where('categoryId').equals(categoryId).toArray()
  let total = 0
  for (const type of types) {
    if (type.deletedAt !== null) continue
    total += await countEntriesForType(type.id, db)
  }
  return total
}

// --- Ordering ---------------------------------------------------------------

export type MoveDirection = -1 | 1

interface Sortable {
  id: string
  sortOrder: number
}

/**
 * Works out the new sortOrder for every sibling after one of them moves.
 *
 * Pure, so the awkward part is testable without a database.
 *
 * It renumbers all siblings densely (0, 1, 2, ...) rather than swapping the
 * two affected values. Swapping looks tidier but breaks the moment sync has
 * delivered two rows with the SAME sortOrder -- a real possibility, since two
 * devices can each create a type offline and both pick the next free number.
 * Swapping equal values is a no-op, so the row would refuse to move with no
 * error to explain why. Dense renumbering always produces a visible move.
 *
 * Dense numbers can collide across categories. That is harmless: type order is
 * only ever compared within a category, because the picker renders one
 * category at a time.
 *
 * Returns only the rows whose sortOrder actually changes, or null when the
 * move runs off either end.
 */
export function reorderSiblings<T extends Sortable>(
  siblings: readonly T[],
  id: string,
  direction: MoveDirection,
): Map<string, number> | null {
  // Tie-break on id so the input order is fully determined even when two rows
  // share a sortOrder; otherwise the result depends on Dexie's iteration order.
  const ordered = [...siblings].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))

  const from = ordered.findIndex((r) => r.id === id)
  if (from === -1) return null

  const to = from + direction
  if (to < 0 || to >= ordered.length) return null

  const moved = ordered[from] as T
  const displaced = ordered[to] as T
  ordered[from] = displaced
  ordered[to] = moved

  const changes = new Map<string, number>()
  ordered.forEach((row, index) => {
    if (row.sortOrder !== index) changes.set(row.id, index)
  })

  return changes
}

/**
 * The only part of a Dexie table this needs: read a row by id, write it back.
 *
 * Structural rather than `EntityTable<T>` because Dexie's InsertType makes the
 * primary key optional on writes, and a generic function cannot satisfy that
 * and read `get(id: string)` at the same time. Narrowing to what is actually
 * used keeps the one cast at the call site, where it is obviously sound: both
 * tables hold rows with an id and a sortOrder.
 */
interface OrderableTable {
  get: (id: string) => Promise<(SyncFields & Sortable) | undefined>
  put: (row: SyncFields & Sortable) => Promise<unknown>
}

async function applyOrder(
  table: OrderableTable,
  changes: Map<string, number>,
  deviceId: string,
): Promise<void> {
  const now = Date.now()
  for (const [id, sortOrder] of changes) {
    const row = await table.get(id)
    if (!row) continue
    await table.put({ ...row, sortOrder, updatedAt: now, deviceId, _dirty: 1 })
  }
}

// --- Categories -------------------------------------------------------------

export async function createCategory(
  name: string,
  colorHex?: string,
  db: BlockyTimeDB = defaultDb,
): Promise<CategoryRow> {
  const trimmed = name.trim()
  if (!trimmed) throw new Error('A category needs a name')

  const existing = await listCategories(true, db)
  const colour =
    normaliseHex(colorHex ?? '') ?? suggestColor(existing.map((c) => c.colorHex))

  const row: CategoryRow = {
    id: crypto.randomUUID(),
    name: trimmed,
    colorHex: colour,
    icon: null,
    sortOrder: existing.length,
    isCollapsed: 0,
    isArchived: 0,
    updatedAt: Date.now(),
    deletedAt: null,
    deviceId: await getDeviceId(db),
    serverSeq: 0,
    _dirty: 1,
  }

  await db.categories.put(row)
  return row
}

export async function updateCategory(
  id: string,
  patch: Partial<Pick<CategoryRow, 'name' | 'colorHex' | 'icon' | 'isArchived' | 'isCollapsed'>>,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  const row = await db.categories.get(id)
  if (!row) return

  await db.categories.put({
    ...row,
    ...patch,
    updatedAt: Date.now(),
    deviceId: await getDeviceId(db),
    _dirty: 1,
  })
}

export async function moveCategory(
  id: string,
  direction: MoveDirection,
  db: BlockyTimeDB = defaultDb,
): Promise<boolean> {
  const siblings = await listCategories(true, db)
  const changes = reorderSiblings(siblings, id, direction)
  if (!changes) return false

  await applyOrder(db.categories as unknown as OrderableTable, changes, await getDeviceId(db))
  return true
}

/**
 * The collapsed/expanded state of a category in the picker.
 *
 * A synced field, not local UI state, because the picker is the main surface
 * of the app and having it laid out differently on each device is a small but
 * constant irritation. Deliberate: 02-data-model.md puts isCollapsed on the
 * row for exactly this reason.
 */
export async function setCategoryCollapsed(
  id: string,
  collapsed: boolean,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  await updateCategory(id, { isCollapsed: collapsed ? 1 : 0 }, db)
}

export async function archiveCategory(
  id: string,
  archived = true,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  await updateCategory(id, { isArchived: archived ? 1 : 0 }, db)
}

/**
 * Soft-deletes a category. Its types survive, uncategorised.
 *
 * Cascading would be the obvious implementation and the wrong one: the types
 * hold every entry's colour and name, so a cascade turns "I reorganised my
 * categories" into "my history is blank". Uncategorised types still appear in
 * the picker under an Uncategorised heading, so nothing becomes unreachable.
 */
export async function deleteCategory(id: string, db: BlockyTimeDB = defaultDb): Promise<void> {
  const deviceId = await getDeviceId(db)
  const now = Date.now()

  const orphans = await db.eventTypes.where('categoryId').equals(id).toArray()
  for (const type of orphans) {
    await db.eventTypes.put({
      ...type,
      categoryId: null,
      updatedAt: now,
      deviceId,
      _dirty: 1,
    })
  }

  const row = await db.categories.get(id)
  if (row) {
    await db.categories.put({ ...row, deletedAt: now, updatedAt: now, deviceId, _dirty: 1 })
  }

  logger.info('db', `Category deleted; ${orphans.length} type(s) became uncategorised`)
}

// --- Event types ------------------------------------------------------------

export interface NewEventType {
  name: string
  categoryId: string | null
  shortcutText?: string
  colorHex?: string
}

export async function createEventType(
  input: NewEventType,
  db: BlockyTimeDB = defaultDb,
): Promise<EventTypeRow> {
  const name = input.name.trim()
  if (!name) throw new Error('An event type needs a name')

  const all = await listEventTypes(true, db)
  const siblings = all.filter((t) => t.categoryId === input.categoryId)

  // Next free slot among ITS OWN siblings, not a global count. A global count
  // would append a new type to the end of every category's list at once.
  const sortOrder = siblings.reduce((max, t) => Math.max(max, t.sortOrder + 1), 0)

  const row: EventTypeRow = {
    id: crypto.randomUUID(),
    categoryId: input.categoryId,
    name,
    shortcutText: (input.shortcutText ?? name).trim(),
    colorHex: normaliseHex(input.colorHex ?? '') ?? suggestColor(all.map((t) => t.colorHex)),
    icon: null,
    defaultObjectId: null,
    sortOrder,
    isPinned: 0,
    isArchived: 0,
    updatedAt: Date.now(),
    deletedAt: null,
    deviceId: await getDeviceId(db),
    serverSeq: 0,
    _dirty: 1,
  }

  await db.eventTypes.put(row)
  return row
}

export async function updateEventType(
  id: string,
  patch: Partial<
    Pick<
      EventTypeRow,
      | 'name'
      | 'shortcutText'
      | 'colorHex'
      | 'icon'
      | 'categoryId'
      | 'defaultObjectId'
      | 'isPinned'
      | 'isArchived'
    >
  >,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  const row = await db.eventTypes.get(id)
  if (!row) return

  await db.eventTypes.put({
    ...row,
    ...patch,
    updatedAt: Date.now(),
    deviceId: await getDeviceId(db),
    _dirty: 1,
  })
}

/**
 * Moves a type up or down WITHIN ITS CATEGORY.
 *
 * To move it to a different category, change categoryId -- reordering and
 * recategorising are separate ideas and conflating them makes both confusing.
 */
export async function moveEventType(
  id: string,
  direction: MoveDirection,
  db: BlockyTimeDB = defaultDb,
): Promise<boolean> {
  const row = await db.eventTypes.get(id)
  if (!row) return false

  const siblings = (await listEventTypes(true, db)).filter((t) => t.categoryId === row.categoryId)
  const changes = reorderSiblings(siblings, id, direction)
  if (!changes) return false

  await applyOrder(db.eventTypes as unknown as OrderableTable, changes, await getDeviceId(db))
  return true
}

/** Pinned types appear in a row above the categories, for the handful used all day. */
export async function setPinned(
  id: string,
  pinned: boolean,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  await updateEventType(id, { isPinned: pinned ? 1 : 0 }, db)
}

/**
 * Archiving: the answer to "I do not do this any more".
 *
 * The type leaves the picker but keeps every entry attached, so last year's
 * statistics still say what you actually did. This is why deleting a used type
 * is refused -- archiving is almost always what was meant.
 */
export async function archiveEventType(
  id: string,
  archived = true,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  await updateEventType(id, { isArchived: archived ? 1 : 0 }, db)
}

/**
 * Soft-deletes a type, but only when nothing depends on it.
 *
 * Refuses while live entries point at it, and says how many. The alternatives
 * -- archive, or merge into another type -- both keep the history, and one of
 * them is what was wanted every time.
 */
export async function deleteEventType(id: string, db: BlockyTimeDB = defaultDb): Promise<void> {
  const count = await countEntriesForType(id, db)
  if (count > 0) {
    throw new CatalogGuardError(
      count === 1
        ? '1 block still uses this event type.'
        : `${count} blocks still use this event type.`,
      count,
    )
  }

  const row = await db.eventTypes.get(id)
  if (!row) return

  const now = Date.now()
  await db.eventTypes.put({
    ...row,
    deletedAt: now,
    updatedAt: now,
    deviceId: await getDeviceId(db),
    _dirty: 1,
  })
}

/**
 * Merges one event type into another.
 *
 * Every entry pointing at the source is repointed at the target through
 * entryRepository -- the only module allowed to write entries -- and then the
 * source is tombstoned. Returns the entry ChangeSet so the whole merge is one
 * undo step; a merge that could only be half-undone would be worse than none.
 */
export async function mergeEventTypes(
  sourceId: string,
  targetId: string,
  db: BlockyTimeDB = defaultDb,
): Promise<ChangeSet> {
  if (sourceId === targetId) return { before: [], after: [] }

  const target = await db.eventTypes.get(targetId)
  if (!target || target.deletedAt !== null) {
    throw new Error('Cannot merge into an event type that does not exist')
  }

  const change = await reassignType(sourceId, targetId, db)

  const source = await db.eventTypes.get(sourceId)
  if (source) {
    const now = Date.now()
    await db.eventTypes.put({
      ...source,
      deletedAt: now,
      updatedAt: now,
      deviceId: await getDeviceId(db),
      _dirty: 1,
    })
  }

  logger.info('db', `Merged event type: ${change.after.length} block(s) repointed`)
  return change
}
